<script setup>

import { inject, ref, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { 
	apiGetModelesPrives,
  apiCreerNouveauModele,
  apiOuvrirModele,
  apiExporterModele,
  apiImporterModele, 
  apiOublierModificationsModele,
  apiSupprimerModele } from '@/services/ServiceModele.js';
import Listbox from 'primevue/listbox';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ModaleNouveauModele from './ModaleNouveauModele.vue';
import ModaleImportModele from './ModaleImportModele.vue';

const router = useRouter();
const rubriques = [
  {code: 'modeles_prives', libelle: 'Mes modèles', icone: 'fa-laptop-file'},
  {code: 'demandes', libelle: 'Demandes', icone: 'fa-square-check'},
  {code: 'evenements', libelle: 'Derniers événements', icone: 'fa-table-list'},
  {code: 'modeles_publics', libelle: 'Modèles publics', icone: 'fa-globe'}
];

const utilisateur = inject('utilisateur');
const gererErreurApi = inject('gererErreurApi');
const setMessageChargement = inject('setMessageChargement');
const afficherSucces = inject('afficherSucces');
const afficherBarreFlottante = inject('afficherBarreFlottante');
const appelerApiTelechargement = inject('appelerApiTelechargement');

const rubriqueSelectionnee = ref(rubriques[0]);
const boutonMenuEntete = ref(null);
const boutonsMenu = ref([]);
const modelesPrives = ref([]);
const affichageMenuEntete = ref(false);
const modeleMenuAffiche = ref(null);
const affichageModaleNouveauModele = ref(false);
const affichageModaleImportModele = ref(false);

watch(() => utilisateur.value, (utilisateur) => {
  if (utilisateur && !utilisateur.inconnu) {
    rafraichirModelesPrives();
  }
});

function getClasseModele(modele) {
  return {'modele-en-cours-edition': estModeleEnCoursEdition(modele)};
};

function estModeleEnCoursEdition(modele) {
  return !modele.dernierEnregistrement || modele.derniereModification > modele.dernierEnregistrement;
}

function comparerModeles(modele1, modele2) {
  if (estModeleEnCoursEdition(modele1)) {
    if (estModeleEnCoursEdition(modele2)) {
      return modele2.derniereModification - modele1.derniereModification;
    } else {
      return -1;
    }
  } else {
    if (estModeleEnCoursEdition(modele2)) {
      return 1;
    } else {
      return modele2.derniereModification - modele1.derniereModification;
    }
  }
}

function setRefBoutonMenu(indiceLigne, btnMenuModele) {
  boutonsMenu.value[indiceLigne] = btnMenuModele;
}

function basculerAffichageMenuEntete() {
  if (!affichageMenuEntete.value) {
    const itemsMenu = [];
    itemsMenu.push({
      id: 'importer',
      libelle: 'Importer un modèle depuis un fichier',
      icones: ['fa-solid fa-upload'],
      groupe: 1,
      commande: () => ouvrirModaleImportModele()
    });	
    afficherBarreFlottante({
      items: itemsMenu, 
      selectionItems: [],
      composantParent: boutonMenuEntete.value
    });
    affichageMenuEntete.value = true;
  } else {
    afficherBarreFlottante(null);
    affichageMenuEntete.value = false;
  }
}

function basculerAffichageMenuModele(modele, indiceLigne) {
  if (modeleMenuAffiche.value !== modele) {
    afficherBarreFlottante({
      items: getItemsMenuModele(modele), 
      selectionItems: [],
      composantParent: boutonsMenu.value[indiceLigne]
    });
    modeleMenuAffiche.value = modele;
  } else {
    afficherBarreFlottante(null);
    modeleMenuAffiche.value = null;
  }
}

function getItemsMenuModele(modele) {
  const itemsMenu = [];
  if (modele.dernierEnregistrement) {
    if (estModeleEnCoursEdition(modele)) {
      itemsMenu.push({
        id: 'annuler_modifs',
        libelle: 'Annuler les modifications depuis le dernier enregistrement',
        icones: ['fa-solid fa-rotate-left'],
        groupe: 1,
        commande: () => oublierModifications(modele)
      });	
    } else {
      itemsMenu.push({
        id: 'exporter',
        libelle: 'Exporter le modèle',
        icones: ['fa-solid fa-download'],
        groupe: 1,
        commande: () => exporterModele(modele)
      });	
    }
  }
  itemsMenu.push({
    id: 'supprimer',
    libelle: 'Supprimer',
    icones: ['fa-solid fa-trash'],
    style: 'color: rgb(219, 18, 18); ',
    groupe: 2,
    commande: () => supprimer(modele.nomModele)
  });	
  return itemsMenu;
}

function rafraichirModelesPrives() {
  console.log("Récupération de la liste des modèles privés");
  setMessageChargement('Rafraîchissement des modèles en cours...');
  apiGetModelesPrives().then(
    liste => {
      setMessageChargement(null);
      liste.sort(comparerModeles);
      modelesPrives.value = liste;
    }, 
    err => gererErreurApi(err));
}

function ouvrirModaleImportModele() {
  affichageModaleImportModele.value = true;
}

function ouvrirModaleNouveauModele() {
  affichageModaleNouveauModele.value = true;
}

function creerNouveauModele(infosModele) {
	console.log("Nouveau modèle");
  affichageModaleNouveauModele.value = false;
  nextTick(() => {
    setMessageChargement('Création du modèle en cours...');
    apiCreerNouveauModele(infosModele).then(
      idModele => {
        const donneesRouting = router.resolve({path: `/modeles/${idModele}`});
        window.open(donneesRouting.href, '_blank');
        setMessageChargement(null);
      }, 
      err => {
        gererErreurApi(err);
        affichageModaleNouveauModele.value = true;
      });
  });
}

function importerModele(fichier, nomModele) {
  setMessageChargement(`Import du modèle ${nomModele} en cours...`);
  apiImporterModele(fichier, nomModele).then(
    () => {
      setMessageChargement(null);
      affichageModaleImportModele.value = false;
      afficherSucces('Import de modèle terminé');
      rafraichirModelesPrives();
    }, 
    err => gererErreurApi(err));
}

function ouvrir(nomModele) {
	console.log(`Ouverture de ${nomModele}`);
  setMessageChargement('Ouverture du modèle en cours...');
  apiOuvrirModele(nomModele).then(
		idModele => {
      const donneesRouting = router.resolve({path: `/modeles/${idModele}`});
      window.open(donneesRouting.href, '_blank');
      setMessageChargement(null);
		}, 
		err => gererErreurApi(err));
}

function oublierModifications(modele) {
  if (confirm(`Voulez-vous vraiment annuler les modifications du modèle '${modele.nomModele}' depuis le dernier enregistrement ?`)) {
    setMessageChargement('Annulation des modifications en cours...');
    apiOuvrirModele(modele.nomModele).then(
      idModele => {
        apiOublierModificationsModele(idModele).then(
            () => {
              setMessageChargement(null);
              rafraichirModelesPrives();
            },
            err => gererErreurApi(err));
      }, 
      err => gererErreurApi(err));
  }
}

function exporterModele(modele) {
  setMessageChargement('Téléchargement en cours...');
  appelerApiTelechargement(apiExporterModele, [modele.nomModele], (reponseOK) => {
  });
}

function supprimer(nomModele) {
  if (confirm(`Voulez-vous vraiment supprimer le modèle ${nomModele} ?`)) {
    setMessageChargement('Suppression du modèle en cours...');
    apiSupprimerModele(nomModele).then(
      () => {
        setMessageChargement(null);
        rafraichirModelesPrives();
      }, 
      err => gererErreurApi(err));
  }
}

</script>

<template>
  <div class="page-accueil">
    <div class="panel-menu-lateral">
      <Listbox 
        v-model="rubriqueSelectionnee"
        :options="rubriques"
        :selectOnFocus="true">
        <template #option="slotProps">
          <div class="rubrique"><i :class="['icone', 'fa-solid', slotProps.option.icone]" />{{ slotProps.option.libelle }}</div>
        </template>
      </Listbox>
    </div>
    <div class="panel-contenu-rubrique"
      v-if="rubriqueSelectionnee?.code === 'modeles_prives'">
      <div class="entete-liste">
        <label class="titre">Mes modèles</label>
        <button class="bouton nouveau" @click="ouvrirModaleNouveauModele()"><i class="fa-solid fa-circle-plus" style="margin-right: 5px; " />Créer un modèle</button>
      </div>
      <DataTable 
        :value="modelesPrives" 
        :rowClass="getClasseModele"
        paginator :rows="8">
          <template #empty><span style="font-style: italic; ">Aucun modèle</span></template>
          <Column field="nomModele" 
            header="Nom du modèle" 
            headerClass="entete-colonne"
            sortable />
          <Column 
            field="dernierEnregistrement" 
            header="Dernier enregistrement"
            headerClass="entete-colonne"
            sortable>
            <template #body="slotProps">
                <label class="contenu-colonne">{{ slotProps.data.dernierEnregistrement ? (new Date(slotProps.data.dernierEnregistrement)).toLocaleString() : '' }}</label>
            </template>                        
          </Column>
          <Column 
            field="derniereModification" 
            header="Dernière modification"
            headerClass="entete-colonne"
            sortable>
            <template #body="slotProps">
                <label class="contenu-colonne">{{ slotProps.data.derniereModification ? (new Date(slotProps.data.derniereModification)).toLocaleString() : '' }}</label>
            </template>                        
          </Column>
          <Column
            headerClass="entete-colonne">
            <template #header>
              <div class="boutons-entete-tableau">
                <button class="bouton btn-modele" 
                  @click="rafraichirModelesPrives"
                  v-tooltip="'Rafraîchir la liste des modèles'">
                  <i class="fa-solid fa-refresh"/>
                </button>
                <button class="bouton btn-modele fa-solid fa-caret-down" 
                  ref="boutonMenuEntete"
                  @click.stop="basculerAffichageMenuEntete()" />
              </div>
            </template>
            <template #body="slotProps">
              <div class="boutons-modele">
                <button class="bouton btn-modele" 
                  @click="ouvrir(slotProps.data.nomModele)"
                  v-tooltip="'Ouvrir'">
                  <i class="fa-solid fa-folder-open"/>
                </button>
                <button class="bouton btn-modele fa-solid fa-caret-down" 
                  :ref="btnMenuModele => setRefBoutonMenu(slotProps.index, btnMenuModele)"
                  @click.stop="basculerAffichageMenuModele(slotProps.data, slotProps.index)" />
              </div>
            </template>                        
          </Column>
      </DataTable>
      <ModaleNouveauModele 
        v-model:visible="affichageModaleNouveauModele"
        @creation-modele="creerNouveauModele"/>
      <ModaleImportModele 
        v-model:visible="affichageModaleImportModele"
        @import-modele="importerModele"/>
    </div>
    <div class="panel-contenu-rubrique"
     v-else-if="rubriqueSelectionnee?.code === 'modeles_publics'">
        <div class="entete-liste">
        <label class="titre">Modèles publics</label>
      </div>
      <label style="font-size: 14px; font-style: italic; color: var(--couleur-primaire)">(Fonctionnalité à venir)</label>
    </div>
    <div class="panel-contenu-rubrique"
     v-else-if="rubriqueSelectionnee?.code === 'evenements'">
        <div class="entete-liste">
        <label class="titre">Derniers événements</label>
      </div>
      <label style="font-size: 14px; font-style: italic; color: var(--couleur-primaire)">(Fonctionnalité à venir)</label>
    </div>
    <div class="panel-contenu-rubrique"
     v-else-if="rubriqueSelectionnee?.code === 'demandes'">
        <div class="entete-liste">
        <label class="titre">Demandes</label>
      </div>
      <label style="font-size: 14px; font-style: italic; color: var(--couleur-primaire)">(Fonctionnalité à venir)</label>
    </div>
  </div>
</template>

<style scoped>

.page-accueil {
  display: flex;
  flex-direction: row;
}

.panel-menu-lateral {
  display: flex;
  flex-direction: column;
  border-right: solid 3px var(--couleur-bordure);
  min-height: 100vh;
}

.rubrique {
  font-size: 16px;
  padding: 10px 10px;
}

.rubrique .icone {
  margin-right: 10px;
}

.panel-contenu-rubrique {
  display: flex;
  flex-direction: column;
  margin-left: 10%;
  margin-right: 20%;
  flex-grow: 1;
  padding-right: 20px;
}

.entete-liste {
  justify-content: space-between;
  display: flex;
  flex-direction: row;
  margin-top: 20px;
  margin-bottom: 20px;
  align-items: flex-end;
}

.p-listbox {
  border: none;
}

.contenu-colonne {
  cursor: inherit;
}

.titre {
  margin-left: 5px;
  font-size: 20px;
}

.nouveau {
  border: 1px solid var(--couleur-primaire);
}

.boutons-modele .bouton {
  font-size: 10px;
}

.boutons-entete-tableau {
  display: flex;
  flex-direction: row;
  gap: 10px;
  justify-content: center;
  width: 100%;
}

.boutons-modele {
  display: flex;
  flex-direction: row;
  gap: 10px;
  justify-content: center;
  font-size: 10px;
  width: 100%;
}

.btn-modele {
  width: 30px;
}
</style>
